import React, { useEffect, useState } from 'react';
import useScrollReveal from '../hooks/useScrollReveal';

const TermsPage = () => {
  const [mounted, setMounted] = useState(false);

  useScrollReveal({ threshold: 0.1, rootMargin: '-50px', oneWay: true });

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    window.scrollTo(0, 0);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div style={{ background: 'var(--bg-primary)', paddingTop: '80px' }}>
      {/* Hero */}
      <section style={{ padding: 'clamp(6rem, 12vw, 8rem) 0' }}>
        <div className="container-narrow" style={{ textAlign: 'center' }}>
          <h1
            style={{
              fontSize: 'clamp(3rem, 6vw, 5rem)',
              lineHeight: '1.1',
              marginBottom: 'var(--space-lg)',
              fontFamily: 'var(--font-serif)',
              fontStyle: 'italic',
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 1s var(--ease-out) 0.2s',
            }}
          >
            Terms of Service
          </h1>
          <p
            style={{
              fontSize: '1.1rem',
              color: 'var(--text-secondary)',
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 1s var(--ease-out) 0.4s',
            }}
          >
            Effective Date: May 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section style={{ padding: 'clamp(4rem, 8vw, 6rem) 0', background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ maxWidth: '800px' }}>

          {/* Intro */}
          <div data-scroll>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-lg)' }}>
              Welcome to Allyverto.
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-lg)' }}>
              These Terms of Service ("Terms") govern your access to and use of the applications, websites, and AI-powered services provided by Allyverto IT Technologies Private Limited ("Allyverto", "we", "our", or "us").
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              By accessing or using our services, you agree to these Terms. If you do not agree, please do not use our services.
            </p>
          </div>

          {/* 1. Eligibility */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              1. Eligibility
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              Our services are intended for users who are at least 13 years old.
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              By using our platform, you confirm that:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)', paddingLeft: '1.5rem' }}>
              <li>You are at least 13 years of age</li>
              <li>You are legally permitted to use our services under applicable laws</li>
              <li>The information you provide is accurate and truthful</li>
            </ul>
          </div>

          {/* 2. Our Services */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              2. Our Services
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              Allyverto provides AI-powered conversational and productivity services that may include:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', paddingLeft: '1.5rem' }}>
              <li>AI chatbot interactions</li>
              <li>Image and document analysis</li>
              <li>Voice-to-text interactions</li>
              <li>Personalized AI experiences</li>
              <li>Learning and productivity assistance</li>
            </ul>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              Our services continuously evolve, and features may change, improve, or be removed over time.
            </p>
          </div>

          {/* 3. User Accounts */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              3. User Accounts
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              To access certain features, users may create an account using:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', paddingLeft: '1.5rem' }}>
              <li>Google Sign-In</li>
              <li>Traditional email/password authentication</li>
            </ul>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              You are responsible for:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', paddingLeft: '1.5rem' }}>
              <li>Maintaining the confidentiality of your account</li>
              <li>Protecting your login credentials</li>
              <li>All activity occurring under your account</li>
            </ul>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              You agree not to:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)', paddingLeft: '1.5rem' }}>
              <li>Share your account with others</li>
              <li>Impersonate another individual or organization</li>
              <li>Create accounts using false information</li>
            </ul>
          </div>

          {/* 4. Acceptable Use */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              4. Acceptable Use
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              You agree to use our services responsibly and lawfully.
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              You must not use the platform to:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', paddingLeft: '1.5rem' }}>
              <li>Generate or distribute illegal content</li>
              <li>Share sexually explicit or exploitative material</li>
              <li>Promote violence, hate, harassment, or abuse</li>
              <li>Attempt unauthorized access to systems or accounts</li>
              <li>Distribute malware or harmful code</li>
              <li>Violate intellectual property rights</li>
              <li>Misuse AI-generated outputs for unlawful purposes</li>
              <li>Circumvent platform safety systems or restrictions</li>
            </ul>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              We reserve the right to suspend or terminate accounts that violate these rules.
            </p>
          </div>

          {/* 5. AI-Generated Content */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              5. AI-Generated Content
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              Our services use artificial intelligence systems to generate responses and assist users.
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              You understand and agree that:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', paddingLeft: '1.5rem' }}>
              <li>AI-generated responses may sometimes be inaccurate or incomplete</li>
              <li>Outputs should not be treated as professional legal, medical, financial, or emergency advice</li>
              <li>Users are responsible for evaluating AI-generated information before relying on it</li>
            </ul>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              We continuously improve our systems but do not guarantee perfect accuracy or uninterrupted availability.
            </p>
          </div>

          {/* 6. User Content */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              6. User Content
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              You retain ownership of the content you upload or submit, including:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', paddingLeft: '1.5rem' }}>
              <li>Messages</li>
              <li>Images</li>
              <li>Documents</li>
              <li>Prompts</li>
              <li>Other user-generated content</li>
            </ul>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              By using our services, you grant Allyverto a limited license to process and store such content solely for:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', paddingLeft: '1.5rem' }}>
              <li>Delivering requested functionality</li>
              <li>Maintaining service operations</li>
              <li>Improving user experience</li>
              <li>Ensuring platform safety and reliability</li>
            </ul>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              You are responsible for ensuring that uploaded content does not violate laws or third-party rights.
            </p>
          </div>

          {/* 7. Privacy */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              7. Privacy
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              Your use of our services is also governed by our Privacy Policy.
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              Please review our Privacy Policy to understand how we collect, process, and protect user information.
            </p>
          </div>

          {/* 8. Permissions and Device Access */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              8. Permissions and Device Access
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              Some features may require access to:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', paddingLeft: '1.5rem' }}>
              <li>Camera</li>
              <li>Microphone</li>
              <li>Location services</li>
              <li>File storage</li>
            </ul>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              These permissions are optional and are only used when initiated or authorized by the user.
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              Users may revoke permissions through their device settings at any time.
            </p>
          </div>

          {/* 9. Service Availability */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              9. Service Availability
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              We strive to keep our services available and reliable, but we do not guarantee:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', paddingLeft: '1.5rem' }}>
              <li>Continuous uptime</li>
              <li>Error-free operation</li>
              <li>Uninterrupted access</li>
            </ul>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              We may temporarily suspend services for:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)', paddingLeft: '1.5rem' }}>
              <li>Maintenance</li>
              <li>Security updates</li>
              <li>Infrastructure upgrades</li>
              <li>Policy enforcement</li>
            </ul>
          </div>

          {/* 10. Intellectual Property */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              10. Intellectual Property
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              All platform technology, branding, software, designs, and AI systems provided by Allyverto remain the intellectual property of Allyverto IT Technologies Private Limited unless otherwise stated.
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              Users may not:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)', paddingLeft: '1.5rem' }}>
              <li>Copy or redistribute platform code</li>
              <li>Reverse engineer systems</li>
              <li>Reproduce branding without permission</li>
              <li>Use our intellectual property in misleading ways</li>
            </ul>
          </div>

          {/* 11. Account Suspension and Termination */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              11. Account Suspension and Termination
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              We reserve the right to:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', paddingLeft: '1.5rem' }}>
              <li>Suspend accounts</li>
              <li>Restrict access</li>
              <li>Remove content</li>
              <li>Terminate services</li>
            </ul>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              if users violate these Terms, applicable laws, or platform safety policies.
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              Users may also stop using the services at any time.
            </p>
          </div>

          {/* 12. Limitation of Liability */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              12. Limitation of Liability
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              To the maximum extent permitted by law, Allyverto IT Technologies Private Limited shall not be liable for:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', paddingLeft: '1.5rem' }}>
              <li>Indirect or consequential damages</li>
              <li>Loss of data</li>
              <li>Service interruptions</li>
              <li>AI-generated inaccuracies</li>
              <li>User misuse of generated content</li>
            </ul>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              Use of the platform is at the user's own risk.
            </p>
          </div>

          {/* 13. Changes to These Terms */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              13. Changes to These Terms
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              We may update these Terms from time to time.
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              Updated versions will be posted with a revised effective date. Continued use of the services after changes constitutes acceptance of the updated Terms.
            </p>
          </div>

          {/* 14. Contact Us */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              14. Contact Us
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              If you have questions regarding these Terms, contact:
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              <strong>Allyverto IT Technologies Private Limited</strong>
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)' }}>
              Email:{' '}
              <a href="mailto:admin@allyverto.com" style={{ color: 'var(--accent)', borderBottom: '1px solid var(--accent)' }}>
                admin@allyverto.com
              </a>
            </p>
          </div>

        </div>
      </section>
    </div>
  );
};

export default TermsPage;
